import React, { useEffect } from "react";
import { useRouteError } from "react-router-dom";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

function isChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message;
  const name = error.name;
  return (
    message?.includes("Failed to fetch dynamically imported module") ||
    message?.includes("loading dynamically imported module") ||
    message?.includes("Loading chunk") ||
    name === "ChunkLoadError"
  );
}

function useHandleAppError(error: unknown): void {
  useEffect(() => {
    if (!error) return;

    if (isChunkError(error)) {
      window.location.reload();
      return;
    }

    if ((window as any).shopify) {
      (window as any).shopify.toast.show("An unexpected error occurred.", { isError: true });
    } else {
      console.error("Unknown error caught by boundary:", error);
    }
  }, [error]);
}

function ChunkErrorFallback({ error }: FallbackProps) {
  useHandleAppError(error);
  return null;
}

export function AppRouteErrorBoundary() {
  const error = useRouteError();
  useHandleAppError(error);
  return null;
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={ChunkErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}
